import { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { Box } from '@mui/material';
import usePricing from 'api/usePricing';
import usePublisher from 'api/usePublisher';
import { EIntervalsRepeat, EWeekDay } from 'constants/enums';
import {
  dayInMonthOptions,
  exchangeRateSchedulerIntervalsRepeatOptions
} from 'utils/selectOptionsUtils';

import Button from 'components/Button/Button';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Modal } from 'components/Modal/Modal';



const HARD_CODED_TIME_FRAMES = [
  EIntervalsRepeat.NONE,
  EIntervalsRepeat.QUARTERLY,
  EIntervalsRepeat.SEMI_ANNUALLY,
  EIntervalsRepeat.ANNUALLY
];

interface ExchangeRateSchedulerModalProps {
  isOpen: boolean;
  setIsExchangeRateSchedulerModalOpen: (open: boolean) => void;
}

const ExchangeRateSchedulerModal: React.FC<ExchangeRateSchedulerModalProps> = ({
  isOpen,
  setIsExchangeRateSchedulerModalOpen
}) => {
  const { updateExchangeRatesScheduler } = usePricing();
  const { getPublisherSettings } = usePublisher();
  const [frequency, setFrequency] = useState<EIntervalsRepeat>(
    EIntervalsRepeat.NONE
  );
  const [hour, setHour] = useState<number>(0);
  const [weeklyFrequencyFactor, setWeeklyFrequencyFactor] = useState<number>(0);
  const [monthlyFrequencyFactor, setMonthlyFrequencyFactor] =
    useState<number>(0);
  const submitUpdateExchangeRatesScheduler = () => {
    updateExchangeRatesScheduler.mutate(
      {
        frequency,
        hour,
        weeklyFrequencyFactor,
        monthlyFrequencyFactor
      },
      {
        onSuccess: () => {
          getPublisherSettings.refetch();
          toast.success('Exchange rates scheduled successfully');
        },
        onError: () => {
          toast.error('Failed to schedule exchange rates');
        },
        onSettled: () => {
          setIsExchangeRateSchedulerModalOpen(false);
        }
      }
    );
  };

  useEffect(() => {
    if (getPublisherSettings.data) {
      setFrequency(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.frequency ||
          EIntervalsRepeat.NONE
      );
      setHour(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.hour || 0
      );
      setWeeklyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.weeklyFrequencyFactor || 0
      );
      setMonthlyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.monthlyFrequencyFactor || 0
      );
    }
  }, [getPublisherSettings.data]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsExchangeRateSchedulerModalOpen(false)}
      width="600px"
      className="!z-[999]"
    >
      <Box className="w-full flex flex-col gap-4">
        <Box className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">Schedule Exchange Rates</h2>
          <p className="text-sm text-gray-600">
            All existing price points will be updated according to the latest
            exchange rate. Players will automatically see the new prices at the
            store
          </p>
        </Box>

        <ExchangeRateSchedulerContent
          schedulerReapet={frequency}
          setSchedulerRepeat={setFrequency}
          weeklyFrequencyFactor={weeklyFrequencyFactor}
          setWeeklyFrequencyFactor={setWeeklyFrequencyFactor}
          monthlyFrequencyFactor={monthlyFrequencyFactor}
          setMonthlyFrequencyFactor={setMonthlyFrequencyFactor}
          hour={hour}
          setHour={setHour}
        />

        <Box className="flex gap-2 justify-end">
          <Button
            onClick={() => setIsExchangeRateSchedulerModalOpen(false)}
            variant="outline"
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => submitUpdateExchangeRatesScheduler()}
            variant="default"
            type="button"
          >
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExchangeRateSchedulerModal;

const ExchangeRateSchedulerContent: React.FC<{
  schedulerReapet: EIntervalsRepeat;
  setSchedulerRepeat: (repeat: EIntervalsRepeat) => void;
  weeklyFrequencyFactor?: number;
  setWeeklyFrequencyFactor: (factor: number) => void;
  monthlyFrequencyFactor?: number;
  setMonthlyFrequencyFactor: (factor: number) => void;
  hour?: number;
  setHour: (hour: number) => void;
}> = ({
  schedulerReapet,
  setSchedulerRepeat,
  weeklyFrequencyFactor,
  setWeeklyFrequencyFactor,
  monthlyFrequencyFactor,
  setMonthlyFrequencyFactor,
  hour,
  setHour
}) => {
  return (
    <Box className="m-2 p-8">
      <Box className="flex flex-col gap-4">
        <InputSelectDropdown
        shouldHighlightSelectedOption
          label="Repeat"
          contentClassName="z-[9999]"
          options={exchangeRateSchedulerIntervalsRepeatOptions.map((item) => ({
            value: String(item.value),
            label: item.content as string
          }))}
          defaultSelected={
            exchangeRateSchedulerIntervalsRepeatOptions
              .map((item) => ({
                value: String(item.value),
                label: item.content as string
              }))
              .find((opt) => opt.value === String(schedulerReapet)) || {
              value: '',
              label: ''
            }
          }
          onOptionChange={(value) => {
            setSchedulerRepeat(value as EIntervalsRepeat);
          }}
        />
        {!HARD_CODED_TIME_FRAMES.includes(schedulerReapet) && (
          <Box className="mt-2 flex flex-row gap-2">
            {schedulerReapet !== EIntervalsRepeat.DAILY && (
              <Box style={{ width: '100%' }}>
                {schedulerReapet === EIntervalsRepeat.WEEKLY && (
                  <InputSelectDropdown
                  shouldHighlightSelectedOption
                    label="Weekday"
                    options={Object.keys(EWeekDay)
                      .filter((k) => isNaN(Number(k)))
                      .map((day, i) => ({
                        value: String(i),
                        label:
                          day.charAt(0).toUpperCase() +
                          day.slice(1).toLowerCase()
                      }))}
                    defaultSelected={
                      Object.keys(EWeekDay)
                        .filter((k) => isNaN(Number(k)))
                        .map((day, i) => ({
                          value: String(i),
                          label:
                            day.charAt(0).toUpperCase() +
                            day.slice(1).toLowerCase()
                        }))
                        .find((opt) => opt.value === String(weeklyFrequencyFactor)) || {
                        value: '',
                        label: ''
                      }
                    }
                    onOptionChange={(value) => {
                      setWeeklyFrequencyFactor(Number(value));
                    }}
                  />
                )}
                {schedulerReapet === EIntervalsRepeat.MONTHLY && (
                  <InputSelectDropdown
                  shouldHighlightSelectedOption
                    contentClassName="z-[9999]"
                    label="Day of month"
                    options={dayInMonthOptions.map((dayObject, i) => ({
                      value: String(i),
                      label: String(dayObject.content)
                    }))}
                    defaultSelected={
                      dayInMonthOptions
                        .map((dayObject, i) => ({
                          value: String(i),
                          label: String(dayObject.content)
                        }))
                        .find((opt) => opt.value === String(monthlyFrequencyFactor)) || {
                        value: '',
                        label: ''
                      }
                    }
                    onOptionChange={(value) => {
                      setMonthlyFrequencyFactor(Number(value));
                    }}
                  />
                )}
              </Box>
            )}
            <Box
              style={{
                width: '100%'
              }}
            >
              <InputSelectDropdown
              shouldHighlightSelectedOption
                contentClassName="z-[9999]"
                label="Time (UTC)"
                options={Array.from({ length: 24 }, (_, i: number) => ({
                  value: String(i),
                  label: `${i < 10 ? '0' : ''}${i}:00`
                }))}
                defaultSelected={
                  Array.from({ length: 24 }, (_, i: number) => ({
                    value: String(i),
                    label: `${i < 10 ? '0' : ''}${i}:00`
                  })).find((opt) => opt.value === String(hour)) || {
                    value: '',
                    label: ''
                  }
                }
                onOptionChange={(value) => setHour(Number(value))}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
